import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Bot, CalendarDays, CalendarPlus, CheckCircle2, Clock, List, LockKeyhole, Megaphone, Phone,
  PhoneCall, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, Play, UserRoundPlus, Users,
} from 'lucide-react';
import { appointmentsAPI, callsAPI, contactsAPI, workspaceAPI } from '../../api/client';
import { useTenant } from '../../context/TenantContext';
import { StatusPill } from './AppointmentsPage';
import { seatsUsed, UNLIMITED_MEMBERS } from '../../lib/limits';
import CallDialog, { prettyNumber } from './CallDialog';
import { formatDuration } from './calls/callModel';
import { CallStatus } from './calls/components/CallBadges';
import { formatTime, useBasic } from './ui';

function nextResetDate() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 1).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

function timeAgo(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(`${iso}Z`).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function Sparkline({ color, values }) {
  const max = Math.max(1, ...values);
  const step = 170 / Math.max(1, values.length - 1);
  const y = (v) => 35 - (v / max) * 29;
  return (
    <svg className="sparkline" viewBox="0 0 170 38" preserveAspectRatio="none">
      <polyline points={values.map((v, i) => `${i * step},${y(v)}`).join(' ')} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ChartLine({ color, values, width = 400, height = 125 }) {
  const x = (i) => i * (width / Math.max(1, values.length - 1));
  return (
    <g>
      <polyline points={values.map((v, i) => `${x(i)},${height - v}`).join(' ')} fill="none" stroke={color} strokeWidth="2.1" strokeLinejoin="round" strokeLinecap="round" />
      {values.length <= 31 && values.map((v, i) => <circle key={i} cx={x(i)} cy={height - v} r="2.4" fill={color} />)}
    </g>
  );
}

const SERIES = [
  ['Total Calls', '#10b981', 'total'],
  ['AI Handled', '#2563eb', null], // AI answering is a later phase: always zero for now
  ['Human Handled', '#f59e0b', 'answered'],
  ['Missed Calls', '#f43f5e', 'missed'],
];
const PERIODS = { 'Last 30 Days': () => 30, 'Last 7 Days': () => 7, 'This Month': () => Math.max(7, new Date().getDate()) };
const SOURCE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#9333ea', '#718096'];

function PerformanceChart({ active, daily }) {
  if (!daily) return <div className="chart-wrap chart-empty">Loading…</div>;
  const key = SERIES[active][2];
  const values = daily.map((d) => (key ? d[key] : 0));
  const top = Math.max(5, Math.ceil(Math.max(...values) / 5) * 5);
  const labelAt = [0, 0.2, 0.4, 0.6, 0.8, 1].map((f) => Math.round(f * (daily.length - 1)));
  const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  return (
    <div className="chart-wrap">
      <div className="y-labels">{[1, 0.8, 0.6, 0.4, 0.2, 0].map((f) => <span key={f}>{Math.round(top * f)}</span>)}</div>
      <svg className="performance-svg" viewBox="0 0 400 140" preserveAspectRatio="none">
        {[0, 25, 50, 75, 100, 125].map((y) => <line key={y} x1="0" y1={y + 5} x2="400" y2={y + 5} stroke="#e8edf5" strokeDasharray="3 4" />)}
        <ChartLine key={`${active}-${daily.length}`} color={SERIES[active][1]} values={values.map((v) => (v / top) * 120)} />
      </svg>
      <div className="x-labels">{labelAt.map((i) => <span key={i}>{fmt(daily[i].date)}</span>)}</div>
    </div>
  );
}

function SourceDonut({ sources, total }) {
  if (!sources) return <p className="panel-empty">Loading…</p>;
  const top = sources.slice(0, 4);
  const rest = sources.slice(4).reduce((n, s) => n + s.count, 0);
  const slices = rest ? [...top, { source: 'Others', count: rest }] : top;
  let acc = 0;
  const stops = slices.map((s, i) => {
    const from = (acc / total) * 100;
    acc += s.count;
    return `${SOURCE_COLORS[i % SOURCE_COLORS.length]} ${from}% ${(acc / total) * 100}%`;
  });
  return (
    <div className="source-content">
      <div className="donut" aria-label="Calls by lead source" style={total ? { background: `conic-gradient(${stops.join(',')})` } : { background: '#e8edf5' }}>
        <div className="donut-hole"><b>{total}</b><span>Total Calls</span></div>
      </div>
      <div className="source-legend">
        {slices.map((s, i) => (
          <div key={s.source}><i style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} /><span>{s.source}</span><b>{Math.round((s.count / total) * 100)}%</b></div>
        ))}
        {!total && <p className="panel-empty">No calls this month yet.</p>}
      </div>
    </div>
  );
}

const talkTime = (s) => (s == null ? null : s < 3600 ? formatDuration(s) : `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`);

export default function DashboardHome() {
  const navigate = useNavigate();
  const { notify, upgrade, plan, upgradePlan, company, memberCount } = useBasic();
  const { tenant, can } = useTenant();
  const [period, setPeriod] = useState('Last 30 Days');
  const [series, setSeries] = useState(0);
  const [leads, setLeads] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [recentCalls, setRecentCalls] = useState(null);
  const [daily, setDaily] = useState(null);
  const [callStats, setCallStats] = useState(null);
  const [counts, setCounts] = useState({});
  const [ai, setAi] = useState(null);
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    contactsAPI.list({ limit: 4 }).then((res) => setLeads(res.data.items)).catch(() => setLeads([]));
    appointmentsAPI.list({ start: new Date().toISOString(), limit: 20 })
      .then((res) => setUpcoming(res.data.items.filter((a) => a.status === 'scheduled' || a.status === 'confirmed').slice(0, 5)))
      .catch(() => setUpcoming([]));
    callsAPI.list({ limit: 5 }).then((res) => setRecentCalls(res.data.items)).catch(() => setRecentCalls([]));
    callsAPI.stats().then((res) => setCallStats(res.data)).catch(() => setCallStats({ total_this_month: 0, by_source: [] }));
    contactsAPI.stats().then((res) => setCounts((c) => ({ ...c, leads: res.data.new_this_month }))).catch(() => {});
    appointmentsAPI.stats().then((res) => setCounts((c) => ({ ...c, appointments: res.data.booked_this_month }))).catch(() => {});
    workspaceAPI.aiSettings().then((res) => setAi(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setDaily(null);
    callsAPI.daily(PERIODS[period]()).then((res) => setDaily(res.data)).catch(() => setDaily([]));
  }, [period]);

  const soon = (label) => notify(`${label} is coming soon.`);
  const maxMembers = tenant.max_members;
  const team = memberCount == null ? '…' : seatsUsed(memberCount, maxMembers);
  const teamPct = memberCount == null || !maxMembers || maxMembers >= UNLIMITED_MEMBERS ? 0 : Math.min(100, Math.round((memberCount / maxMembers) * 100));

  const metrics = [
    ['phone', 'Total Calls', callStats?.total_this_month, 'This month', '#10b981', daily?.slice(-11).map((d) => d.total)],
    ['bot', 'AI Handled', '—', 'AI answering coming soon', '#9333ea', null],
    ['users', 'Leads Captured', counts.leads, 'This month', '#2563eb', null],
    ['calendar', 'Appointments', counts.appointments, 'This month', '#f97316', null],
  ];

  // This month's calls by direction and outcome; each opens Call History with that filter.
  const summary = [
    ['Incoming', callStats?.inbound_this_month, PhoneIncoming, '#2563eb', 'direction=inbound'],
    ['Outgoing', callStats?.outbound_this_month, PhoneOutgoing, '#9333ea', 'direction=outbound'],
    ['Answered', callStats?.answered_this_month, PhoneCall, '#10b981', 'status=answered'],
    ['Missed', callStats?.missed_this_month, PhoneMissed, '#f59e0b', 'status=missed'],
    ['Failed', callStats?.failed_this_month, PhoneOff, '#f43f5e', 'status=failed'],
    ['Talk time', talkTime(callStats?.talk_time_seconds), Clock, '#0891b2', 'status=answered'],
  ];

  return (
    <div className="dashboard-grid">
      <section className="center-column">
        <div className="metric-grid">
          {metrics.map(([type, label, value, caption, color, spark]) => (
            <div className="metric-card" key={label}>
              <div className="metric-icon" style={{ '--metric': color }}>
                {type === 'phone' ? <Phone size={22} fill={color} color={color} />
                  : type === 'bot' ? <Bot size={23} color={color} />
                    : type === 'users' ? <Users size={23} color={color} />
                      : <CalendarDays size={22} color={color} />}
              </div>
              <div className="metric-info"><span>{label}</span><b>{value ?? '…'}</b><small className="metric-caption">{caption}</small></div>
              {spark && spark.length > 1 && <Sparkline color={color} values={spark} />}
            </div>
          ))}
        </div>

        <div className="call-summary" aria-label="Calls this month">
          {summary.map(([label, value, Icon, color, filter]) => (
            <button key={label} type="button" style={{ '--c': color }} onClick={() => navigate(`/calls?${filter}&range=month`)} title={`${label} calls this month`}>
              <i><Icon size={15} /></i>
              <span><small>{label}</small><b>{value ?? '…'}</b></span>
            </button>
          ))}
        </div>

        <div className="middle-grid">
          <section className="panel performance-panel">
            <div className="panel-heading">
              <h2>Call Performance</h2>
              <select value={period} onChange={(e) => setPeriod(e.target.value)}>{Object.keys(PERIODS).map((p) => <option key={p}>{p}</option>)}</select>
            </div>
            <div className="chart-tabs" role="tablist">
              {SERIES.map(([label, color], i) => (
                <button key={label} role="tab" aria-selected={series === i} className={series === i ? 'selected' : ''}
                  style={series === i ? { background: color } : undefined} onClick={() => setSeries(i)}>{label}</button>
              ))}
            </div>
            <PerformanceChart active={series} daily={daily} />
            <div className="legend">
              {SERIES.map(([label, color], i) => (
                <span key={label} className={series === i ? 'legend-on' : 'legend-off'}><i style={{ background: color }} />{label}</span>
              ))}
            </div>
          </section>
          <section className="panel source-panel">
            <div className="panel-heading"><h2>Call Source</h2></div>
            <SourceDonut sources={callStats?.by_source} total={callStats?.total_this_month || 0} />
          </section>
        </div>

        <div className="bottom-grid">
          <section className="panel recent-calls">
            <div className="panel-heading"><h2>Recent Calls</h2><button className="text-button" onClick={() => navigate('/calls')}>View All <ArrowRight size={13} /></button></div>
            <div className="table-scroll">
              <table>
                <thead><tr><th>Time</th><th>Caller</th><th>Type</th><th>Duration</th><th>Team member</th><th>Status</th></tr></thead>
                <tbody>
                  {recentCalls?.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/calls/${c.id}`)} title="Call details">
                      <td>{formatTime(c.started_at)}</td>
                      <td><span className="caller"><span className="mini-phone"><Phone size={11} /></span>{c.contact_name || prettyNumber(c.other_number)}</span></td>
                      <td><span className={c.direction === 'inbound' ? 'type-incoming' : 'type-outgoing'}>
                        {c.direction === 'inbound' ? <PhoneIncoming size={12} /> : <PhoneOutgoing size={12} />}{c.direction === 'inbound' ? 'Incoming' : 'Outgoing'}
                      </span></td>
                      <td>{formatDuration(c.duration_seconds)}</td>
                      <td>{c.user_name || '—'}</td>
                      <td><CallStatus status={c.status} /></td>
                    </tr>
                  ))}
                  {recentCalls === null && <tr><td colSpan={6} className="empty-state">Loading…</td></tr>}
                  {recentCalls?.length === 0 && <tr><td colSpan={6} className="empty-state">No calls yet.{can('calls.make') && <> <button className="link-button" onClick={() => setCalling(true)}>Make a call</button></>}</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
          <section className="panel appointments-panel">
            <div className="panel-heading"><h2>Upcoming Appointments</h2><button className="text-button" onClick={() => navigate('/appointments?view=calendar')}>View Calendar <ArrowRight size={13} /></button></div>
            <div className="appointment-list">
              {upcoming === null && <p className="panel-empty">Loading…</p>}
              {upcoming?.length === 0 && (
                <p className="panel-empty">No upcoming appointments. <button className="link-button" onClick={() => navigate('/appointments?new=1')}>Book one</button></p>
              )}
              {upcoming?.map((a) => {
                const d = new Date(`${a.starts_at}Z`);
                return (
                  <button className="appointment-row" key={a.id} onClick={() => navigate('/appointments')}>
                    <div className="date-block"><b>{d.getDate()}</b><small>{d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()}</small></div>
                    <div className="appointment-copy"><small>{formatTime(a.starts_at)}</small><b>{a.title}</b><span>{a.contact_name ? `With ${a.contact_name}` : a.location || ''}</span></div>
                    <StatusPill status={a.status} />
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <section className="panel quick-panel">
          <div className="panel-heading"><h2>Quick Actions</h2></div>
          <div className="quick-actions">
            {[
              [PhoneCall, 'Make a Call', 'mint', () => (can('calls.make') ? setCalling(true) : soon('Calling for your role'))],
              [UserRoundPlus, 'Add Contact', 'blue', () => navigate('/contacts?new=1')],
              [List, 'View Calls', 'purple', () => navigate('/calls')],
              [CalendarPlus, 'Book Appointment', 'orange', () => navigate('/appointments?new=1')],
              [Bot, 'Configure AI', 'pink', () => navigate('/ai-receptionist')],
              [Megaphone, 'Create Campaign', 'locked', () => notify(`Upgrade to ${upgradePlan} to create campaigns.`)],
            ].map(([Icon, label, cls, onClick]) => (
              <button className={`quick-action ${cls}`} key={label} onClick={onClick}>
                <Icon size={23} />
                <b>{cls === 'locked' && <LockKeyhole size={13} />} {label}</b>
                {cls === 'locked' && <small>Upgrade to {upgradePlan}</small>}
              </button>
            ))}
          </div>
        </section>
      </section>

      <aside className="right-column">
        <section className="panel ai-panel">
          <div className="panel-heading">
            <h2>AI Receptionist</h2><span className={`active-pill ${ai && !ai.enabled ? 'off' : ''}`}><i />{ai && !ai.enabled ? 'Off' : 'On'}</span>
            <button className="manage-button" onClick={() => navigate('/ai-receptionist')}>Manage <ArrowRight size={12} /></button>
          </div>
          <div className="ai-body">
            <div className="robot-art">
              <div className="robot-head"><div className="robot-ear left" /><div className="robot-ear right" /><div className="robot-face"><span /><span /></div><div className="robot-mouth" /></div>
              <div className="robot-neck" /><div className="robot-shirt" />
            </div>
            <div className="ai-checks">
              <b>Your AI receptionist will</b>
              {['Answer customer queries', 'Capture leads', 'Book appointments', 'Hand over to your team'].map((x) => (
                <span key={x}><CheckCircle2 size={13} fill="#10b981" color="#fff" />{x}</span>
              ))}
            </div>
          </div>
          <button className="greeting" onClick={() => navigate('/ai-receptionist')}>
            <span><b>{ai?.name || 'AI greeting'}</b><small>"{ai?.greeting || `Hi! Welcome to ${company}. How can I assist you today?`}"</small></span>
            <span className="play"><Play size={13} fill="currentColor" /></span>
          </button>
        </section>

        <section className="panel usage-panel">
          <div className="panel-heading"><h2>Plan Usage <span>({plan})</span></h2><small>Resets on {nextResetDate()}</small></div>
          <div className="usage-list">
            {[['phone', 'Calls this month', callStats ? String(callStats.total_this_month) : '…', null], ['users', 'Team Members', team, teamPct]].map(([type, label, value, pct]) => (
              <div className="usage-row" key={label}>
                <div className="usage-icon">{type === 'users' ? <Users size={19} /> : <Phone size={19} />}</div>
                <div className="usage-info"><div><span>{label}</span><b>{value}</b></div>{pct != null && <div className="usage-bar"><i style={{ width: `${pct}%` }} /></div>}</div>
                {pct != null && <b className="usage-pct">{pct}%</b>}
              </div>
            ))}
          </div>
          <button className="upgrade-full" onClick={upgrade}>👑 Upgrade to {upgradePlan} <ArrowRight size={14} /></button>
        </section>

        <section className="panel leads-panel">
          <div className="panel-heading"><h2>Recent Leads</h2><button className="text-button" onClick={() => navigate('/contacts')}>View All <ArrowRight size={13} /></button></div>
          <div className="lead-list">
            {leads === null && <p className="panel-empty">Loading…</p>}
            {leads?.length === 0 && (
              <p className="panel-empty">No contacts yet. <button className="link-button" onClick={() => navigate('/contacts?new=1')}>Add your first contact</button></p>
            )}
            {leads?.map((c) => (
              <div className="lead-row" key={c.id}>
                <div className={`lead-avatar ${c.status}`}>{initials(c.name)}</div>
                <div className="lead-main"><b>{c.name}</b><span>{c.phone || c.email}</span></div>
                <div className="lead-meta"><small>{timeAgo(c.created_at)}</small><span>{c.source || '—'}</span></div>
                <span className={`lead-tag ${c.status}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </section>
      </aside>

      {calling && (
        <CallDialog onClose={() => setCalling(false)}
          onPlaced={(call) => { setCalling(false); notify(`Calling your phone now. Answer it to be connected to ${call.contact_name || prettyNumber(call.other_number)}.`); }} />
      )}
    </div>
  );
}
